import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Handshake, Percent, Gift, Clock, ArrowRight, CheckCircle2, Loader2, Calculator, FileText, Radar, Lightbulb, Medal, QrCode, GraduationCap, Globe2, Search, Repeat, Briefcase } from 'lucide-react';
import SEO from '../components/SEO';
import { PartnerLeaderboard } from '../components/partner/PartnerLeaderboard';
import { HeroSlideshow } from '../components/HeroSlideshow';
import { ShareEarnBar } from '../components/ShareEarnBar';
import { useT } from '../i18n';
import { trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const HERO_SLIDES = [
  '/affiliates/slide-pitch.png',
  '/heros/orange-kitchen.jpg',
  '/affiliates/slide-win.png',
  '/heros/green-terrace.jpg',
  '/heros/city-evening.jpg',
  '/affiliates/slide-freedom.png',
  '/heros/market-color.jpg',
];

const AMBITION_IMG = 'https://images.unsplash.com/photo-1543269664-56d93c1b41a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwb3duZXIlMjB0YWJsZXQlMjBtb2Rlcm58ZW58MHx8fHwxNzgzMzk1ODcwfDA&ixlib=rb-4.1.0&q=85';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55, ease: 'easeOut' },
};

const PLANS = [
  { id: 'index', name: 'Index AI™', price: 99 },
  { id: 'workforce', name: 'AI Workforce™', price: 199 },
  { id: 'catering', name: 'Catering AI™', price: 399 },
];

// One-off projects: partner earns 30% of every approved budget, once it is paid in full.
const PROJECTS = [
  { id: 'website', en: 'Restaurant Website', es: 'Web de Restaurante', price: 1499 },
  { id: 'ordering', en: 'Online Ordering Setup', es: 'Setup de Pedidos Online', price: 899 },
  { id: 'brand', en: 'Brand & Menu Design', es: 'Branding y Diseño de Carta', price: 699 },
  { id: 'automation', en: 'Custom AI Automation', es: 'Automatización IA a medida', price: 1250 },
];

function CommissionCalculator({ isEs }) {
  const [n, setN] = useState(5);
  const [selected, setSelected] = useState(['workforce']);
  const [projects, setProjects] = useState(['website']);
  const toggle = (id) => setSelected((p) => (p.includes(id) ? (p.length > 1 ? p.filter((x) => x !== id) : p) : [...p, id]));
  const toggleProj = (id) => setProjects((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const recurringPer = PLANS.filter((p) => selected.includes(p.id)).reduce((s, p) => s + p.price, 0);
  const projectPer = PROJECTS.filter((p) => projects.includes(p.id)).reduce((s, p) => s + p.price, 0);
  const monthly = Math.round(n * recurringPer * 0.3);
  const oneoff = Math.round(n * projectPer * 0.3);
  const year1 = monthly * 12 + oneoff;
  const fmt = (v) => `$${v.toLocaleString('en-US')}`;
  const names = PLANS.filter((p) => selected.includes(p.id)).map((p) => p.name).join(' + ');
  return (
    <motion.div {...fadeUp} className="rounded-2xl bg-[#101317]/80 backdrop-blur-xl border border-white/[0.06] shadow-2xl p-6 sm:p-8" data-testid="commission-calculator">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#F77F11]">
          <Calculator className="w-4 h-4" /> {isEs ? 'Calculadora de comisiones' : 'Commission calculator'}
        </p>
        <span className="px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/10 text-neutral-400 text-[10px] font-bold tracking-wide" data-testid="calc-example-badge">
          {isEs ? 'EJEMPLO ILUSTRATIVO' : 'ILLUSTRATIVE EXAMPLE'}
        </span>
      </div>

      <p className="mt-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-neutral-300">
        <Repeat className="w-4 h-4 text-[#FF9B4A]" />
        {isEs ? 'Suscripciones mensuales — 30% recurrente' : 'Monthly subscriptions — 30% recurring'}
      </p>
      <p className="mt-1 text-[12.5px] text-neutral-500 leading-relaxed">
        {isEs
          ? 'Cobras cada mes, mientras el cliente sea nuestro y pague su cuota. Marca lo que contrataría tu referido:'
          : 'You get paid every single month, for as long as the client stays with us and pays. Select what your referral would take:'}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {PLANS.map((p) => {
          const on = selected.includes(p.id);
          return (
            <button key={p.id} onClick={() => toggle(p.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[12.5px] font-semibold border transition-all duration-200 ${on ? 'bg-[#F77F11] text-white border-[#F77F11]' : 'bg-white/[0.04] text-neutral-400 border-white/15 hover:border-white/40'}`}
              data-testid={`calc-plan-${p.id}`}>
              {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-neutral-500" />}
              {p.name} · ${p.price}{isEs ? '/mes' : '/mo'}
            </button>
          );
        })}
      </div>

      <p className="mt-6 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-neutral-300">
        <Briefcase className="w-4 h-4 text-[#FF9B4A]" />
        {isEs ? 'Proyectos one-off — 30% de cada presupuesto' : 'One-off projects — 30% of every budget'}
      </p>
      <p className="mt-1 text-[12.5px] text-neutral-500 leading-relaxed">
        {isEs
          ? 'Además de lo recurrente: ganas el 30% de cada presupuesto aprobado, en cuanto el cliente lo paga en su totalidad. Y seguiremos añadiendo servicios:'
          : 'On top of recurring: you earn 30% of every approved budget, as soon as the client pays it in full. And we keep adding services:'}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {PROJECTS.map((p) => {
          const on = projects.includes(p.id);
          return (
            <button key={p.id} onClick={() => toggleProj(p.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[12.5px] font-semibold border transition-all duration-200 ${on ? 'bg-[#F77F11] text-white border-[#F77F11]' : 'bg-white/[0.04] text-neutral-400 border-white/15 hover:border-white/40'}`}
              data-testid={`calc-project-${p.id}`}>
              {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-neutral-500" />}
              {isEs ? p.es : p.en} · {fmt(p.price)}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <div className="flex justify-between text-[13px] text-neutral-400 mb-1.5">
          <span>{isEs ? 'Restaurantes referidos' : 'Restaurants referred'}</span>
          <span className="font-bold text-white tabular-nums" data-testid="calc-count">{n}</span>
        </div>
        <input type="range" min="1" max="50" value={n} onChange={(e) => setN(Number(e.target.value))}
          className="w-full accent-[#F77F11]" data-testid="calc-slider" />
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-4">
          <motion.p key={monthly} initial={{ scale: 0.92, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 18 }}
            className="text-3xl font-bold text-[#FF9B4A] tabular-nums tracking-tight" data-testid="calc-monthly">{fmt(monthly)}</motion.p>
          <p className="text-[12px] text-neutral-500">{isEs ? 'al mes, recurrente — mientras paguen' : 'per month, recurring — while they pay'}</p>
        </div>
        <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-4">
          <motion.p key={oneoff} initial={{ scale: 0.92, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 18 }}
            className="text-3xl font-bold text-white tabular-nums tracking-tight" data-testid="calc-oneoff">{fmt(oneoff)}</motion.p>
          <p className="text-[12px] text-neutral-500">{isEs ? 'en proyectos pagados al 100%' : 'from projects paid in full'}</p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-[#F77F11]/20 to-white/[0.04] border border-[#F77F11]/30 p-4">
          <motion.p key={year1} initial={{ scale: 0.92, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 18 }}
            className="text-3xl font-bold text-white tabular-nums tracking-tight" data-testid="calc-year1">{fmt(year1)}</motion.p>
          <p className="text-[12px] text-neutral-400">{isEs ? 'tu primer año, en total' : 'your first year, total'}</p>
        </div>
      </div>
      <p className="mt-3 text-[11.5px] text-neutral-500" data-testid="calc-formula">
        {isEs
          ? `Ejemplo: ${n} restaurante${n > 1 ? 's' : ''} × ${names} ($${recurringPer}/mes) × 30% recurrente${projectPer ? ` + 30% de $${projectPer.toLocaleString('en-US')} en proyectos one-off por restaurante` : ''}. Base 30% — hasta 40% en nivel Gold. Sin techo.`
          : `Example: ${n} restaurant${n > 1 ? 's' : ''} × ${names} ($${recurringPer}/mo) × 30% recurring${projectPer ? ` + 30% of $${projectPer.toLocaleString('en-US')} in one-off projects per restaurant` : ''}. 30% base — up to 40% at Gold tier. No cap.`}
      </p>
    </motion.div>
  );
}

function PartnerArsenal({ isEs }) {
  const items = isEs ? [
    { icon: Search, t: 'Index AI Scanner™ — tu producto de entrada', d: 'Comparte tu link del scanner: el restaurante escanea su web gratis, ve su score real y deja sus datos. El lead queda a tu nombre y tú cobras el 30% recurrente.' },
    { icon: FileText, t: 'Generador de Propuestas PDF', d: 'Escribe un restaurante y su web → escaneamos su presencia en vivo → PDF profesional con su score real, hallazgos y ROI. Listo para WhatsApp en 2 minutos.' },
    { icon: Radar, t: 'Radar de leads en vivo', d: 'Cada restaurante que entra por tu enlace y usa el Score™ aparece en tu radar — con su puntuación y su potencial de ingresos.' },
    { icon: Lightbulb, t: 'Coaching de ventas diario', d: 'Una idea de venta nueva cada día + recomendaciones personalizadas según tus números. Y un resumen semanal por email.' },
    { icon: Medal, t: 'Niveles Bronze → Silver → Gold', d: 'Empiezas con 30% recurrente. Con 3 clientes subes a Silver (35%). Con 10, Gold (40%).' },
    { icon: GraduationCap, t: 'Activación guiada de 5 días', d: 'Al aprobarse tu solicitud, recibes 5 emails con misiones diarias de 10 minutos: tu primer pitch, tu primera propuesta, tu primer seguimiento.' },
    { icon: QrCode, t: 'Kit completo de materiales', d: 'QR personal, plantillas de WhatsApp/email, widgets para tu web y munición de ventas con noticias del sector.' },
  ] : [
    { icon: Search, t: 'Index AI Scanner™ — your door-opener product', d: 'Share your scanner link: the restaurant scans its website free, sees its real score and leaves its info. The lead is credited to you and you earn 30% recurring.' },
    { icon: FileText, t: 'Proposal PDF Generator', d: 'Type a restaurant and its website → we scan its presence live → professional PDF with its real score, findings and ROI. WhatsApp-ready in 2 minutes.' },
    { icon: Radar, t: 'Live lead radar', d: 'Every restaurant that comes through your link and runs the Score™ shows up on your radar — with its score and revenue potential.' },
    { icon: Lightbulb, t: 'Daily sales coaching', d: 'A fresh sales idea every day + personalized recommendations based on your numbers. Plus a weekly email digest.' },
    { icon: Medal, t: 'Bronze → Silver → Gold tiers', d: 'Start at 30% recurring. 3 customers takes you to Silver (35%). 10 makes you Gold (40%).' },
    { icon: GraduationCap, t: '5-day guided activation', d: 'Once approved, you get 5 emails with daily 10-minute missions: your first pitch, your first proposal, your first follow-up.' },
    { icon: QrCode, t: 'Complete asset kit', d: 'Personal QR, WhatsApp/email templates, embeddable widgets and sales ammo with sector news.' },
  ];
  return (
    <div data-testid="partner-arsenal">
      <motion.p {...fadeUp} className="text-xs tracking-[0.2em] uppercase font-bold text-[#F77F11]">
        {isEs ? 'Tu arsenal de ventas — incluido gratis' : 'Your sales arsenal — included free'}
      </motion.p>
      <motion.h2 {...fadeUp} className="mt-3 text-3xl sm:text-4xl tracking-tight font-bold text-white max-w-xl">
        {isEs ? 'No vendes en solitario. Nunca.' : "We never leave you alone."}
      </motion.h2>
      <motion.p {...fadeUp} className="mt-3 text-[14.5px] text-neutral-400 max-w-lg leading-relaxed">
        {isEs ? 'Te damos las armas que usa un equipo comercial profesional — y un coach que te escribe cada semana.' : 'You get the weapons of a professional sales team — and a coach who writes to you every week.'}
      </motion.p>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((it, i) => (
          <motion.div key={it.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.45, delay: (i % 2) * 0.08 }}
            className="group rounded-2xl bg-[#1B1F24] border border-white/[0.04] p-6 hover:border-[#F77F11]/50 hover:-translate-y-1 transition-all duration-300 hover:shadow-2xl hover:shadow-[#F77F11]/10">
            <span className="w-10 h-10 rounded-xl bg-[#F77F11]/10 flex items-center justify-center">
              <it.icon className="w-5 h-5 text-[#FF9B4A]" />
            </span>
            <p className="mt-4 font-bold text-white text-[15px]">{it.t}</p>
            <p className="mt-1.5 text-[13px] text-neutral-400 leading-relaxed">{it.d}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function BentoStats({ isEs }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="partner-bento-stats">
      <motion.div {...fadeUp} className="md:col-span-2 rounded-2xl bg-[#1B1F24] border border-white/[0.04] p-8 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#F77F11]/10 blur-3xl" />
        <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-neutral-500"><Percent className="w-4 h-4 text-[#FF9B4A]" /> {isEs ? 'Tu comisión' : 'Your commission'}</p>
        <p className="mt-3 text-6xl sm:text-7xl font-bold text-white tracking-tighter">30<span className="text-[#FF9B4A]">%</span> <span className="text-2xl sm:text-3xl text-neutral-400 font-semibold">→ 40%</span></p>
        <p className="mt-2 text-[14px] text-neutral-400 max-w-md leading-relaxed">
          {isEs ? 'Recurrente, de todo lo que contrate tu referido, cada mes, mientras siga siendo cliente. Sube a 35% (Silver) y 40% (Gold) al crecer.' : 'Recurring, on everything your referral contracts, every month, for as long as they stay. Climbs to 35% (Silver) and 40% (Gold) as you grow.'}
        </p>
      </motion.div>
      <motion.div {...fadeUp} className="rounded-2xl bg-[#1B1F24] border border-white/[0.04] p-7">
        <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-neutral-500"><Gift className="w-4 h-4 text-[#FF9B4A]" /> {isEs ? 'Para tu referido' : 'For your referral'}</p>
        <p className="mt-3 text-5xl font-bold text-white tracking-tighter">10<span className="text-[#FF9B4A]">%</span></p>
        <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">{isEs ? 'De descuento en cualquier servicio durante 1 año. Tú ganas, ellos ganan.' : 'Off any service for 1 full year. You win, they win.'}</p>
      </motion.div>
      <motion.div {...fadeUp} className="rounded-2xl bg-[#1B1F24] border border-white/[0.04] p-7">
        <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-neutral-500"><Clock className="w-4 h-4 text-[#FF9B4A]" /> Cookie</p>
        <p className="mt-3 text-5xl font-bold text-white tracking-tighter">90 <span className="text-2xl text-neutral-400">{isEs ? 'días' : 'days'}</span></p>
        <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">{isEs ? 'Toda visita con tu enlace queda atribuida a ti durante 90 días.' : 'Every visit through your link is attributed to you for 90 days.'}</p>
      </motion.div>
    </div>
  );
}

function HowItWorks({ isEs }) {
  const steps = isEs ? [
    ['Aplica al programa', 'Envíanos tus datos. Revisamos y aprobamos cada solicitud manualmente en 24-48h.'],
    ['Comparte tu enlace', 'Recibes tu enlace único, tu QR, el generador de propuestas y toda tu artillería de ventas.'],
    ['Cobra cada mes', 'Cuando tu referido contrata, ganas comisión recurrente. Lo ves todo en vivo en tu dashboard.'],
  ] : [
    ['Apply to the program', 'Send us your details. We review and approve every application manually within 24-48h.'],
    ['Share your link', 'You get your unique link, your QR, the proposal generator and your full sales arsenal.'],
    ['Get paid monthly', 'When your referral signs up, you earn recurring commission. Track it all live in your dashboard.'],
  ];
  return (
    <div data-testid="partner-how-it-works">
      <motion.p {...fadeUp} className="text-xs tracking-[0.2em] uppercase font-bold text-[#F77F11]">{isEs ? 'Cómo funciona' : 'How it works'}</motion.p>
      <div className="mt-7 relative pl-8">
        <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-[#F77F11] via-[#F77F11]/40 to-transparent" />
        <div className="space-y-9">
          {steps.map(([t, d], i) => (
            <motion.div key={t} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.12 }} className="relative">
              <span className="absolute -left-8 top-1 w-[23px] h-[23px] rounded-full bg-[#0B0D12] border-2 border-[#F77F11] flex items-center justify-center">
                <span className="w-2 h-2 rounded-full bg-[#F77F11] shadow-[0_0_12px_rgba(247,127,17,0.9)]" />
              </span>
              <p className="font-bold text-white text-[16px]">{i + 1}. {t}</p>
              <p className="mt-1 text-[13.5px] text-neutral-400 leading-relaxed max-w-md">{d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WorldwideAmbition({ isEs }) {
  const marqueeText = isEs
    ? 'PARTNERS EN TODO EL MUNDO • EN CADA CIUDAD • EN CADA PAÍS • '
    : 'PARTNERS WORLDWIDE • IN EVERY CITY • IN EVERY COUNTRY • ';
  return (
    <div data-testid="partner-worldwide">
      <div className="relative overflow-hidden py-4 border-y border-white/[0.06] select-none" aria-hidden="true">
        <div className="whitespace-nowrap animate-[kxmarquee_28s_linear_infinite] will-change-transform">
          {[0, 1].map((k) => (
            <span key={k} className="text-4xl sm:text-5xl font-bold tracking-tighter text-transparent inline-block pr-4"
              style={{ WebkitTextStroke: '1px rgba(255,155,74,0.45)' }}>{marqueeText}{marqueeText}</span>
          ))}
        </div>
        <style>{'@keyframes kxmarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}'}</style>
      </div>
      <div className="mt-8 grid md:grid-cols-2 gap-6 items-center">
        <motion.div {...fadeUp} className="relative rounded-2xl overflow-hidden border border-white/[0.06]">
          <img src={AMBITION_IMG} alt={isEs ? 'Partner con restaurante' : 'Partner with restaurant'} loading="lazy"
            className="w-full h-64 object-cover grayscale-[35%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-transparent to-transparent" />
          <div className="absolute inset-0 bg-[#F77F11]/10 mix-blend-overlay" />
          <p className="absolute bottom-4 left-5 text-white font-bold text-[15px]">
            <Globe2 className="w-4 h-4 inline mr-1.5 text-[#FF9B4A]" />
            {isEs ? 'Sé el partner fundador de tu ciudad' : 'Be the founding partner of your city'}
          </p>
        </motion.div>
        <motion.div {...fadeUp}>
          <p className="text-[14.5px] text-neutral-300 leading-relaxed">
            {isEs
              ? 'Cada ciudad del mundo tiene cientos de restaurantes invisibles para la IA. El primero que llegue con el Score™ en la mano, gana. El leaderboard mundial está abierto — y todavía está vacío.'
              : 'Every city on earth has hundreds of restaurants invisible to AI. Whoever gets there first with the Score™ in hand, wins. The worldwide leaderboard is open — and it is still empty.'}
          </p>
        </motion.div>
      </div>
      <PartnerLeaderboard isEs={isEs} dark />
    </div>
  );
}

export default function AffiliatesPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', city: '', audience: '', password: '', website: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [verifyStep, setVerifyStep] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');

  const c = isEs ? {
    badge: 'Programa de Partners · Mundial', h1a: 'Gana el', h1b: '30% recurrente', h1c: 'recomendando KITXENS',
    sub: 'Recomienda nuestro Restaurant Operating Intelligence a restaurantes y cobra cada mes, mientras sigan siendo clientes. Con radar de leads, propuestas PDF y coaching diario incluidos.',
    heroCta: 'Quiero mi plaza de partner', heroCta2: 'Ver cuánto puedo ganar',
    formTitle: 'Solicita tu plaza', formSub: 'Aprobación manual — te contactamos en 24-48h.',
    name: 'Nombre completo', email: 'Email', phone: 'Teléfono / WhatsApp', company: 'Empresa (opcional)',
    city: 'Ciudad y país (para noticias y aperturas de tu zona)',
    audience: '¿Cómo llegarás a restaurantes? (tu red, contenido, cartera de clientes…)',
    password: 'Contraseña para tu dashboard (mín. 8 caracteres)',
    submit: 'Enviar solicitud', sending: 'Enviando…',
    doneT: '¡Solicitud recibida!', doneD: 'Revisaremos tus datos y te contactaremos en 24-48h. Cuando se apruebe tu solicitud podrás entrar a tu dashboard de partner.',
    login: '¿Ya formas parte del programa? Entra a tu dashboard',
    errGeneric: 'Algo salió mal. Inténtalo de nuevo.',
  } : {
    badge: 'Partner Program · Worldwide', h1a: 'Earn', h1b: '30% recurring', h1c: 'by referring KITXENS',
    sub: 'Refer our Restaurant Operating Intelligence to restaurants and get paid every month, for as long as they stay. Live lead radar, PDF proposals and daily coaching included.',
    heroCta: 'Claim my partner seat', heroCta2: 'See how much I can earn',
    formTitle: 'Apply now', formSub: 'Manual approval — we reply within 24-48h.',
    name: 'Full name', email: 'Email', phone: 'Phone / WhatsApp', company: 'Company (optional)',
    city: 'City & country (for openings and news in your area)',
    audience: 'How will you reach restaurants? (your network, content, client portfolio…)',
    password: 'Password for your dashboard (min. 8 characters)',
    submit: 'Submit application', sending: 'Sending…',
    doneT: 'Application received!', doneD: "We'll review your details and get back to you within 24-48h. Once approved you'll access your affiliate dashboard.",
    login: 'Already an affiliate? Open your dashboard',
    errGeneric: 'Something went wrong. Please try again.',
  };

  const set = (k) => (e) => { setForm((p) => ({ ...p, [k]: e.target.value })); setErr(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || form.password.length < 8) {
      setErr(isEs ? 'Completa nombre, email, teléfono y una contraseña de 8+ caracteres.' : 'Fill in name, email, phone and an 8+ character password.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/affiliates/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, locale: lang }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof data?.detail === 'string' ? data.detail : c.errGeneric); setBusy(false); return; }
      if (data.status === 'verify') { setVerifyStep(true); setBusy(false); return; }
      trackEvent('affiliate_signup', { step: 'registered' });
      setDone(true);
    } catch { setErr(c.errGeneric); setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) { setErr(isEs ? 'Introduce el código de 6 dígitos.' : 'Enter the 6-digit code.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/affiliates/verify-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, code: code.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof data?.detail === 'string' ? data.detail : c.errGeneric); setBusy(false); return; }
      setVerifyStep(false); trackEvent('affiliate_signup', { step: 'verified' }); setDone(true);
    } catch { setErr(c.errGeneric); setBusy(false); }
  };

  const resend = async () => {
    setErr('');
    const r = await fetch(`${API}/api/affiliates/resend-code`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: form.email }),
    });
    if (r.ok) setErr(isEs ? 'Código reenviado — revisa tu email.' : 'Code resent — check your inbox.');
    else setErr(isEs ? 'Espera un minuto antes de pedir otro código.' : 'Wait a minute before requesting another code.');
  };

  const inputCls = 'w-full h-12 rounded-xl border border-white/15 bg-[#1B1F24] px-4 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-[#F77F11] transition-colors';
  const scrollToForm = () => document.getElementById('partner-apply')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToCalc = () => document.querySelector('[data-testid="commission-calculator"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  return (
    <div className="min-h-screen bg-[#0B0D12]" data-testid="affiliates-page">
      <SEO path="/affiliates" title={isEs ? 'Programa de Partners | Kitxens' : 'Partner Program | Kitxens'} />

      {/* ---- Cinematic hero (slideshow) ---- */}
      <section className="relative overflow-hidden md:min-h-[620px] md:h-[82vh] md:flex md:flex-col md:justify-center" data-testid="partner-hero">
        <HeroSlideshow slides={HERO_SLIDES} interval={5000} testId="partner-hero-slideshow" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-[#0B0D12]/60 to-[#0B0D12]" />
        <div className="absolute inset-0 bg-black/40 md:hidden" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pt-40 pb-16 md:pt-32 md:pb-0">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-[#FF9B4A] text-xs tracking-[0.24em] uppercase font-bold">
            <Handshake className="w-4 h-4" /> {c.badge}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 text-4xl sm:text-6xl lg:text-7xl tracking-tighter font-bold leading-[1.05] text-white max-w-3xl [text-shadow:0_2px_18px_rgba(0,0,0,0.45)]">
            {c.h1a} <span className="text-[#FF9B4A]">{c.h1b}</span><br />{c.h1c}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22 }}
            className="mt-6 text-base sm:text-lg text-neutral-300 leading-relaxed max-w-xl">
            {c.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.34 }}
            className="mt-9 flex flex-wrap gap-3">
            <button onClick={scrollToForm} data-testid="partner-hero-cta"
              className="inline-flex items-center gap-2 h-13 py-3.5 px-7 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white font-bold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-[#F77F11]/20">
              {c.heroCta} <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={scrollToCalc} data-testid="partner-hero-cta-calc"
              className="inline-flex items-center gap-2 h-13 py-3.5 px-7 rounded-full border border-white/25 hover:border-white/60 text-white font-semibold text-[15px] transition-all duration-300 backdrop-blur-sm">
              {c.heroCta2}
            </button>
          </motion.div>
          <motion.ul initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.46 }}
            className="mt-9 flex flex-wrap gap-x-7 gap-y-2.5 text-[14px] text-neutral-200 font-medium" data-testid="partner-hero-earnings">
            {(isEs ? [
              '$209/mes por restaurante con el stack completo — $2,509 al año',
              'Cobras cada mes, mientras el cliente sea nuestro y pague',
              '+30% de cada proyecto one-off aprobado y pagado al 100%',
              'Sin techo · sin exclusividad · hasta 40% en Gold',
            ] : [
              '$209/mo per restaurant on the full stack — $2,509 a year',
              'Paid every month, for as long as the client stays and pays',
              '+30% of every one-off project approved and paid in full',
              'No cap · no exclusivity · up to 40% at Gold tier',
            ]).map((t) => (
              <li key={t} className="inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#FF9B4A] shrink-0" /> {t}
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ---- Main grid: scrollytelling left + sticky form right ---- */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-28 pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="col-span-1 lg:col-span-7 flex flex-col gap-20 lg:gap-24 min-w-0">
            <BentoStats isEs={isEs} />
            <CommissionCalculator isEs={isEs} />
            <HowItWorks isEs={isEs} />
            <PartnerArsenal isEs={isEs} />
            <WorldwideAmbition isEs={isEs} />
          </div>

          <div className="col-span-1 lg:col-span-5 relative" id="partner-apply">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="lg:sticky lg:top-24 rounded-2xl bg-[#101317]/80 backdrop-blur-xl border border-white/[0.06] shadow-2xl p-8">
              {done ? (
                <div className="text-center py-10" data-testid="affiliate-register-success">
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                  <h2 className="mt-4 text-xl font-bold text-white">{c.doneT}</h2>
                  <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{c.doneD}</p>
                  <Link to="/affiliates/dashboard" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF9B4A] hover:underline" data-testid="affiliate-goto-dashboard">
                    {c.login} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : verifyStep ? (
                <form onSubmit={verify} className="text-center py-6" data-testid="affiliate-verify-form">
                  <h2 className="text-xl font-bold text-white">{isEs ? 'Verifica tu email' : 'Verify your email'}</h2>
                  <p className="mt-2 text-[13.5px] text-neutral-400 leading-relaxed">
                    {isEs ? `Te enviamos un código de 6 dígitos a ${form.email}. Introdúcelo para confirmar que eres tú (nada de bots por aquí 😉).` : `We sent a 6-digit code to ${form.email}. Enter it to confirm it's really you (no bots allowed 😉).`}
                  </p>
                  <input value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setErr(''); }}
                    inputMode="numeric" autoComplete="one-time-code" placeholder="······"
                    className="mt-5 w-48 mx-auto h-14 rounded-xl border-2 border-white/20 bg-[#1B1F24] focus:border-[#F77F11] outline-none text-center text-2xl font-bold tracking-[0.5em] text-white"
                    data-testid="affiliate-code-input" />
                  {err && <p className="mt-3 text-[13px] text-red-400" data-testid="affiliate-verify-error">{err}</p>}
                  <button type="submit" disabled={busy}
                    className="mt-5 w-full h-12 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white font-semibold text-sm disabled:opacity-50 transition-colors" data-testid="affiliate-verify-btn">
                    {busy ? (isEs ? 'Verificando…' : 'Verifying…') : (isEs ? 'Verificar y enviar solicitud' : 'Verify & submit application')}
                  </button>
                  <button type="button" onClick={resend} className="mt-3 text-[12.5px] text-neutral-500 hover:text-[#FF9B4A] underline underline-offset-2 transition-colors" data-testid="affiliate-resend-btn">
                    {isEs ? 'Reenviar código' : 'Resend code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={submit} data-testid="affiliate-register-form">
                  <h2 className="text-xl font-bold text-white">{c.formTitle}</h2>
                  <p className="mt-1 text-[13px] text-neutral-500">{c.formSub}</p>
                  <input type="text" value={form.website} onChange={set('website')} name="website" tabIndex="-1" autoComplete="off"
                    className="absolute -left-[9999px] top-0 h-0 w-0 opacity-0" aria-hidden="true" />
                  <div className="mt-6 space-y-3.5">
                    <input value={form.name} onChange={set('name')} placeholder={c.name} className={inputCls} data-testid="aff-name" />
                    <input type="email" value={form.email} onChange={set('email')} placeholder={c.email} className={inputCls} data-testid="aff-email" />
                    <input value={form.phone} onChange={set('phone')} placeholder={c.phone} className={inputCls} data-testid="aff-phone" />
                    <input value={form.company} onChange={set('company')} placeholder={c.company} className={inputCls} data-testid="aff-company" />
                    <input value={form.city} onChange={set('city')} placeholder={c.city} className={inputCls} data-testid="aff-city" />
                    <textarea value={form.audience} onChange={set('audience')} placeholder={c.audience} rows={3}
                      className="w-full rounded-xl border border-white/15 bg-[#1B1F24] px-4 py-3 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-[#F77F11] transition-colors resize-none" data-testid="aff-audience" />
                    <input type="password" value={form.password} onChange={set('password')} placeholder={c.password} className={inputCls} data-testid="aff-password" />
                  </div>
                  {err && <p className="mt-3 text-[12.5px] text-red-400" data-testid="aff-register-error">{err}</p>}
                  <button type="submit" disabled={busy}
                    className="mt-5 w-full h-12 rounded-full bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white font-semibold text-sm transition-all duration-300 hover:shadow-2xl hover:shadow-[#F77F11]/20 inline-flex items-center justify-center gap-2"
                    data-testid="aff-register-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {busy ? c.sending : c.submit}
                  </button>
                  <Link to="/affiliates/dashboard" className="mt-4 block text-center text-[12.5px] text-neutral-500 hover:text-[#FF9B4A] transition-colors" data-testid="aff-login-link">
                    {c.login}
                  </Link>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <ShareEarnBar dark />
    </div>
  );
}
